import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@sshop/i18n';
import type { CategoryNode } from '@sshop/domain';
import { useProductFeed, useProductFilters } from '@/features/catalog';
import { useCategoryTree } from '@/features/categories';
import { useLocale } from '@/shared/hooks/useLocale';
import { routes } from '@/shared/lib/routes';
import { api } from '@/shared/lib/apiClient';
import type { HeaderSearchProps } from './HeaderSearch.types';
import { readSearchHistory, rememberSearch } from './searchHistory';
import { lockSearchBackground } from './lockSearchBackground';

export function useHeaderSearch(onFocusChange: HeaderSearchProps['onFocusChange']) {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  const [top, setTop] = useState(120);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [resultSection, setResultSection] = useState<'products' | 'collections'>('products');
  const [history, setHistory] = useState(readSearchHistory);
  const rootRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const query = value.trim();
  const enabled = open && term.length >= 2;
  const matchesQuery = term === query;
  const feed = useProductFeed({ search: term, page: 1, pageSize: 6, sort: 'relevance' }, enabled);
  const facets = useProductFilters(undefined, term, undefined, enabled);
  const collectionFeed = useQuery({
    queryKey: ['header-collections', locale, term],
    queryFn: ({ signal }) => api.collections.browse({ q: term, sort: 'relevance' }, signal),
    enabled,
    staleTime: 30_000,
  });
  const { data: listingLinks } = useQuery({
    queryKey: ['cms-listing-links', locale],
    queryFn: ({ signal }) => api.pageSections.listingLinks(signal),
    enabled: open,
    staleTime: 30_000,
  });
  const collectionListing = listingLinks?.find((link) => link.type === 'collections')?.url;
  const collectionsUrl = collectionListing
    ? `${collectionListing}?q=${encodeURIComponent(query)}`
    : undefined;
  const { data: tree = [] } = useCategoryTree();
  const categories = useMemo(() => {
    const flatten = (nodes: readonly CategoryNode[]): CategoryNode[] =>
      nodes.flatMap((node) => [node, ...flatten(node.children)]);
    return flatten(tree)
      .filter((node) => node.productCount > 0 && node.imageUrl)
      .sort((a, b) => Number(b.onPage) - Number(a.onPage) || b.productCount - a.productCount)
      .slice(0, 8);
  }, [tree]);
  const loading = query.length >= 2 && (!matchesQuery || feed.isFetching);
  const items =
    enabled && matchesQuery && !feed.isFetching && !feed.isError ? (feed.data?.items ?? []) : [];
  const discovery = query.length < 2;
  const collectionsLoading = query.length >= 2 && (!matchesQuery || collectionFeed.isFetching);
  const collections =
    enabled && matchesQuery && !collectionFeed.isFetching && !collectionFeed.isError
      ? (collectionFeed.data?.items ?? []).slice(0, 6)
      : [];
  const optionUrls = [
    ...(!mobile || resultSection === 'products'
      ? items.map((item) => routes.product(locale, item.slug))
      : []),
    ...(!mobile || resultSection === 'collections'
      ? collections.map((item) => routes.collection(locale, item.slug))
      : []),
  ];
  const optionCount = discovery ? history.length : optionUrls.length;
  const selectResultSection = (section: 'products' | 'collections') => {
    setResultSection(section);
    setActiveIndex(-1);
    if (panelRef.current) {
      const filtersHeight =
        panelRef.current.querySelector<HTMLElement>('[data-search-scroll="filters"]')
          ?.offsetHeight ?? 0;
      panelRef.current.scrollTop = Math.min(panelRef.current.scrollTop, filtersHeight);
    }
  };
  // Async results can change the offset of collection options while navigating.
  useEffect(() => setActiveIndex(-1), [items.length, collections.length]);

  const closeSearch = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
    onFocusChange?.(false);
  }, [onFocusChange]);
  const resetSearch = useCallback(() => {
    setValue('');
    setTerm('');
    setResultSection('products');
    closeSearch();
  }, [closeSearch]);
  useEffect(() => {
    resetSearch();
  }, [location.pathname, location.search, resetSearch]);
  useEffect(() => {
    const timer = window.setTimeout(() => setTerm(query), 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px)');
    const update = () => {
      setMobile(media.matches);
      setActiveIndex(-1);
    };
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useLayoutEffect(() => {
    if (!open) return;
    inputRef.current?.focus({ preventScroll: true });
    const updateTop = () =>
      setTop(
        (rootRef.current?.closest('header')?.getBoundingClientRect().bottom ??
          rootRef.current?.getBoundingClientRect().bottom ??
          112) + 8,
      );
    updateTop();
    window.addEventListener('resize', updateTop);
    return () => window.removeEventListener('resize', updateTop);
  }, [open, mobile]);
  useEffect(() => {
    if (!open) return;
    const unlockBackground = lockSearchBackground();
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !surfaceRef.current?.contains(target))
        closeSearch();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
        if (mobile) restoreFocus();
      }
      if (event.key === 'Tab' && !mobile) {
        const formControls =
          rootRef.current?.querySelectorAll<HTMLElement>('form input, form button');
        const panelControls = surfaceRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled])',
        );
        const first = panelControls?.[0];
        const lastForm = formControls?.[formControls.length - 1];
        if (!event.shiftKey && document.activeElement === lastForm && first) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        } else if (event.shiftKey && document.activeElement === first && lastForm) {
          event.preventDefault();
          lastForm.focus({ preventScroll: true });
        }
      }
      if (event.key === 'Tab' && mobile) {
        const focusable = surfaceRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input',
        );
        if (!focusable?.length) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        }
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      unlockBackground();
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, mobile, closeSearch]);

  const restoreFocus = () => {
    window.requestAnimationFrame(() => {
      if (triggerRef.current && window.getComputedStyle(triggerRef.current).display !== 'none')
        triggerRef.current.focus({ preventScroll: true });
      else rootRef.current?.focus({ preventScroll: true });
    });
  };

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const option = document.getElementById(
      `header-search-${discovery ? 'history' : 'option'}-${activeIndex}`,
    );
    const scroll = mobile ? panelRef.current : option?.closest<HTMLElement>('[data-search-scroll]');
    if (!option || !scroll) return;
    const row = option.getBoundingClientRect();
    const viewport = scroll.getBoundingClientRect();
    const visibleTop = mobile
      ? Math.max(
          viewport.top,
          panelRef.current?.querySelector('[role="tablist"]')?.getBoundingClientRect().bottom ??
            viewport.top,
        )
      : viewport.top;
    // Scroll only the relevant modal section; scrollIntoView can move the page.
    if (row.top < visibleTop) scroll.scrollTop += row.top - visibleTop;
    else if (row.bottom > viewport.bottom) scroll.scrollTop += row.bottom - viewport.bottom;
  }, [open, activeIndex, discovery, mobile]);

  const recordSearch = () => {
    if (query) setHistory(rememberSearch(query));
  };
  const navigateToSearch = (next: string) => {
    setHistory(rememberSearch(next));
    resetSearch();
    navigate(next ? routes.search(locale, next) : routes.catalog(locale));
  };
  const finishNavigation = () => {
    recordSearch();
    resetSearch();
  };
  const handleBlur = (event: React.FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    // Touch browsers can blur to the document before delivering a result click.
    if (next && !rootRef.current?.contains(next) && !surfaceRef.current?.contains(next))
      closeSearch();
  };
  return {
    t,
    locale,
    navigate,
    value,
    setValue,
    setTerm,
    open,
    setOpen,
    mobile,
    top,
    activeIndex,
    setActiveIndex,
    history,
    setHistory,
    rootRef,
    surfaceRef,
    panelRef,
    inputRef,
    triggerRef,
    query,
    enabled,
    matchesQuery,
    feed,
    facets,
    categories,
    loading,
    items,
    collections,
    collectionFeed,
    collectionsLoading,
    collectionsUrl,
    resultSection,
    selectResultSection,
    optionUrls,
    discovery,
    optionCount,
    closeSearch,
    resetSearch,
    navigateToSearch,
    finishNavigation,
    handleBlur,
    onFocusChange,
    restoreFocus,
  };
}
